import React, { useState, useEffect } from 'react';
import { 
  FaCheck, 
  FaTimes, 
  FaClipboardCheck, 
  FaUser, 
  FaPaperPlane, 
  FaExclamationTriangle, 
  FaCheckCircle,
  FaUserTie,
  FaCalendarAlt,
  FaClipboardList,
  FaCommentAlt,
  FaEdit,
  FaSave
} from 'react-icons/fa';

const ChecklistModal = ({ show, onHide, userId, memberIndex = -1, formType, mode = 'preview' }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [editableItems, setEditableItems] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (show && userId) {
      fetchChecklist();
    }
  }, [show, userId, memberIndex, formType, mode]);
  
  // Safety check to prevent null userId errors
  if (show && !userId) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onHide}>
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-xl" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Checklist Error</h2>
            <button onClick={onHide} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
          </div>
          <div className="p-6">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl">Cannot load checklist: Missing user ID</div>
          </div>
        </div>
      </div>
    );
  }

  const fetchChecklist = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      
      // Get existing checklist - use the mode to determine the type (prepare or review)
      const checklistType = mode === 'review' ? 'review' : 'prepare';
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists?userId=${userId}&memberIndex=${memberIndex}&formType=${formType}&type=${checklistType}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(checklistType === 'review' ? 'Review checklist is not available yet. The employee must submit the prepare checklist first.' : 'The employee has not prepared a checklist yet.');
        }
        throw new Error(`Failed to fetch ${checklistType} checklist`);
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      
      // Initialize editable items
      setEditableItems(data.checklist.items.map(item => ({
        response: item.response || '',
        remarks: item.remarks || ''
      })));
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError(err.message || 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteChecklist = async () => {
    if (!checklist) return;
    
    // If in review mode and editing, save changes first
    if (mode === 'review' && isEditing) {
      await handleSaveChanges();
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const adminData = JSON.parse(localStorage.getItem('adminInfo'));
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          adminId: adminData._id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete checklist');
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      setSuccess('Checklist review completed successfully!');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error completing checklist:', err);
      setError(err.message || 'Failed to complete checklist');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSaveChanges = async () => {
    if (!checklist) return;
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const adminData = JSON.parse(localStorage.getItem('adminInfo'));
      
      // Save each item that has been modified
      const savePromises = checklist.items.map(async (item, index) => {
        if (editableItems[index].response !== item.response || editableItems[index].remarks !== item.remarks) {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/update-item`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              checklistId: checklist._id,
              itemIndex: index,
              response: editableItems[index].response,
              remarks: editableItems[index].remarks
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to update item ${index + 1}`);
          }
          
          return await response.json();
        }
        return null;
      });
      
      await Promise.all(savePromises.filter(p => p !== null));
      
      // Refresh the checklist data
      await fetchChecklist();
      
      setIsEditing(false);
      setSuccess('Changes saved successfully!');
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error saving changes:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSubmitReviewChecklist = async () => {
    if (!checklist) return;
    
    // If editing, save changes first
    if (isEditing) {
      await handleSaveChanges();
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const adminData = JSON.parse(localStorage.getItem('adminInfo'));
      
      // Check if all items have responses
      const hasIncompleteItems = editableItems.some(item => !item.response);
      
      if (hasIncompleteItems) {
        throw new Error('All checklist items must have a response (Yes/No) before submission');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          adminId: adminData._id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit review checklist');
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      setSuccess('Review checklist submitted successfully!');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error submitting review checklist:', err);
      setError(err.message || 'Failed to submit review checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const getFormTitle = () => {
    switch (formType) {
      case '1040':
        return '1040 - Individual Tax Return';
      case '1120S':
        return '1120S - S Corporation Tax Return';
      case '1120':
        return '1120 - C Corporation Tax Return';
      case '1065':
        return '1065 - Partnership Tax Return';
      case 'Bookkeeping':
        return 'Bookkeeping';
      default:
        return formType;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">Draft</span>;
      case 'submitted':
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full">Submitted</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">Completed</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">Unknown</span>;
    }
  };

  const renderMetadataItem = (icon, label, value) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-gray-400">{icon}</span>
      <span className="text-sm"><strong className="text-gray-700 dark:text-gray-300">{label}:</strong> <span className="text-gray-600 dark:text-gray-400">{value}</span></span>
    </div>
  );

  const getResponseIcon = (response) => {
    switch (response) {
      case 'Yes': return <FaCheck className="text-green-500" />;
      case 'No': return <FaTimes className="text-red-500" />;
      case 'NA': return <span className="text-yellow-500">N/A</span>;
      default: return <span className="text-gray-400">-</span>;
    }
  };
  
  const handleResponseChange = (index, value) => {
    const updatedItems = [...editableItems];
    updatedItems[index].response = value;
    setEditableItems(updatedItems);
  };
  
  const handleRemarksChange = (index, value) => {
    const updatedItems = [...editableItems];
    updatedItems[index].remarks = value;
    setEditableItems(updatedItems);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onHide}>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <FaClipboardCheck className="w-5 h-5" />
                <h2 className="text-lg font-bold">{getFormTitle()} Checklist</h2>
                {checklist && <span className="ml-2">{getStatusBadge(checklist.status)}</span>}
              </div>
              <div className="mt-1">
                <span className="px-2 py-0.5 bg-white/20 rounded text-xs uppercase flex items-center gap-1 w-fit">
                  <FaUser className="w-3 h-3" />
                  {memberIndex === -1 ? 'Primary Client' : `Member ${memberIndex + 1}`}
                </span>
              </div>
            </div>
            <button onClick={onHide} className="p-2 hover:bg-white/10 rounded-lg"><FaTimes /></button>
          </div>
        </div>
        
        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
              <h5 className="mt-4 font-semibold text-gray-900 dark:text-white">Loading Checklist</h5>
              <p className="text-gray-500 dark:text-gray-400">Please wait while we prepare the checklist...</p>
            </div>
          ) : error ? (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl">
              <FaExclamationTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          ) : success ? (
            <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-xl">
              <FaCheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="font-semibold">Success!</h5>
                <p className="text-sm mt-1">{success}</p>
              </div>
            </div>
          ) : checklist ? (
            <>
              {/* Metadata */}
              <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-xl mb-6">
                <h6 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white mb-3">
                  <FaClipboardList />
                  Checklist Information
                </h6>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    {renderMetadataItem(<FaUserTie />, 'Status', getStatusBadge(checklist.status))}
                    {checklist.submittedBy && renderMetadataItem(<FaUser />, 'Prepared by', `Employee (${checklist.submittedBy.name || 'N/A'})`)}
                  </div>
                  <div>
                    {checklist.submittedAt && renderMetadataItem(<FaCalendarAlt />, 'Submitted on', new Date(checklist.submittedAt).toLocaleString())}
                    {checklist.completedAt && renderMetadataItem(<FaCalendarAlt />, 'Reviewed on', new Date(checklist.completedAt).toLocaleString())}
                  </div>
                </div>
              </div>
              
              {/* Checklist Items */}
              <div className="space-y-3">
                {checklist.items.map((item, index) => {
                  const response = isEditing ? editableItems[index]?.response : item.response;
                  const remarks = isEditing ? editableItems[index]?.remarks : item.remarks;
                  return (
                    <div key={index} className={`p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border-l-4 ${response === 'Yes' ? 'border-green-500' : response === 'No' ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'}`}>
                      <h6 className="flex items-center gap-2 font-medium text-gray-900 dark:text-white mb-3">
                        <span className="w-6 h-6 flex items-center justify-center bg-gray-200 dark:bg-dark-600 rounded text-xs font-bold">{index + 1}</span>
                        {item.text}
                      </h6>
                      
                      {isEditing ? (
                        <>
                          <div className="mb-3">
                            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Response:</label>
                            <div className="flex gap-2">
                              <button onClick={() => handleResponseChange(index, 'Yes')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${editableItems[index]?.response === 'Yes' ? 'bg-green-500 text-white' : 'bg-white dark:bg-dark-800 text-green-600 border border-green-200 dark:border-green-800 hover:bg-green-50'}`}>
                                <FaCheck /> Yes
                              </button>
                              <button onClick={() => handleResponseChange(index, 'No')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${editableItems[index]?.response === 'No' ? 'bg-red-500 text-white' : 'bg-white dark:bg-dark-800 text-red-600 border border-red-200 dark:border-red-800 hover:bg-red-50'}`}>
                                <FaTimes /> No
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">Remarks:</label>
                            <textarea rows={2} value={editableItems[index]?.remarks || ''} onChange={(e) => handleRemarksChange(index, e.target.value)} placeholder="Add any comments or notes here..." className="w-full px-3 py-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Response:</span>
                            <div className="flex items-center gap-1">
                              {getResponseIcon(response)}
                              <span className="text-sm text-gray-700 dark:text-gray-300">{response || 'Not answered'}</span>
                            </div>
                          </div>
                          {remarks && (
                            <div className="mt-2">
                              <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-1">
                                <FaCommentAlt className="w-3 h-3" />
                                <span>Remarks</span>
                              </div>
                              <div className="p-2 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 text-sm text-gray-700 dark:text-gray-300">{remarks}</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Actions */}
              {(mode === 'review' || mode === 'preview') && (
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-dark-700">
                  <button onClick={onHide} className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700">
                    <FaTimes /> Close
                  </button>
                  
                  <div className="flex gap-2">
                    {checklist.status !== 'completed' && (
                      <>
                        {isEditing ? (
                          <button onClick={handleSaveChanges} disabled={submitting} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">
                            {submitting ? 'Saving...' : <><FaSave /> Save Changes</>}
                          </button>
                        ) : (
                          <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 font-medium rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20">
                            <FaEdit /> Edit Responses
                          </button>
                        )}
                        {mode === 'review' && checklist.status === 'draft' && (
                          <button onClick={handleSubmitReviewChecklist} disabled={submitting} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50">
                            {submitting ? 'Submitting...' : <><FaPaperPlane /> Submit Review</>}
                          </button>
                        )}
                      </>
                    )}
                    {mode === 'review' && checklist.status === 'submitted' && (
                      <button onClick={handleCompleteChecklist} disabled={submitting} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50">
                        {submitting ? 'Completing...' : <><FaCheck /> Complete Review</>}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-xl">
              <FaExclamationTriangle />
              <span>No checklist data available for this client/member.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistModal;
